/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../contexts/ThemeContext';
import ErrorBoundary from '../general/ErrorBoundary';
import GeneralNavbar from '../general/GeneralNavbar';
import PsychometricService from '../../services/PsychometricService';
import PsychometricTestNavbar from './PyschometricTestNavbar';
import {PsychometricContext} from '../../contexts/PsychometricContext';

const PsychometricTestStart = ({ 
    goToNextStep, 
    location, 
    history, 
    isOrganization,
    signedID,
    signedFirstName,
    signedLastName,
    signedOrganization,
    signedEmail,
    ...props 
}) => {
    const { theme, fontFamily, colors, fontWeight } = useContext(ThemeContext);
    const { isStudent, setIsStudent } = useContext(PsychometricContext);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const isIframe = location?.search?.includes('iframe=true');

    useEffect(() => {

        const checkUser = async () => {
            try {
                // For organization users - convert string 'true' to boolean
                if (isOrganization === 'true' || isOrganization === true) {
                    console.log("Processing organization user");
                    
                    if (!signedID || !signedFirstName || !signedLastName || !signedOrganization) {
                        console.log("Missing organization parameters");
                        history.push('/');
                        return;
                    }

                    if (isIframe) {
                        console.log("Iframe user detected, skipping verification");
                        setIsStudent(false);
                        setIsLoading(false);
                        console.log("States set for iframe user");
                        return;
                    }

                    console.log("Non-iframe organization user, verifying...");
                    try {
                        const orgResponse = await PsychometricService.getPsychometricUser(
                            signedID,
                            signedFirstName,
                            signedLastName,
                            signedOrganization
                        );
                        if (orgResponse?.user) {
                            console.log("User verified successfully");
                            setIsStudent(orgResponse.user.isStudent);
                            setIsLoading(false);
                        } else {
                            console.error("Invalid organization response");
                            setError('Organization not found');
                            setIsLoading(false);
                        }
                    } catch (error) {
                        console.error("Error in organization verification:", error);
                        setError('Organization not found. Please verify your details.');
                        setIsLoading(false);
                    }
                } 
                // For individual users - convert string 'false' to boolean
                else if (isOrganization === 'false' || isOrganization === false) {
                    console.log("Processing individual user");
                    if (!signedEmail) {
                        console.log("Missing email parameter");
                        history.push('/');
                        return;
                    }
                    setIsStudent(false);
                    setIsLoading(false);
                }
                // If isOrganization is neither true nor 'true'
                else {
                    console.log("Invalid organization status:", isOrganization);
                    setError('Invalid user type');
                    setIsLoading(false);
                }
            } catch (error) {
                console.error("Error in checkUser:", error);
                setError('An error occurred. Please try again later.');
                setIsLoading(false);
            }
        };

        checkUser();

        return () => {
            console.log("PsychometricTestStart cleanup");
        };
    }, [isOrganization, signedID, signedFirstName, signedLastName, signedOrganization, signedEmail, isIframe, history]);

    useEffect(() => {
        console.log("Loading state changed:", isLoading);
    }, [isLoading]);

    if (isLoading) {
        console.log("Rendering loading state");
        return <div>Loading...</div>; 
    }

    console.log("Rendering full component");

    const handleBeginClick = () => {
        setIsLoading(true);
      
        goToNextStep();
    };

    if (error) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">
                    {error}
                    <br />
                    <button 
                        className="btn btn-primary mt-3"
                        onClick={() => history.push('/')}
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: fontFamily.primary }}>
            {isIframe && (
                <ErrorBoundary errorAt="At PsychometricTestStart PsychometricTestNavbar">
                    <PsychometricTestNavbar signedOrganization={signedOrganization} />
                </ErrorBoundary>
            )}
            {!isIframe && (
                <ErrorBoundary errorAt="At General PsychometricTestStart GeneralNavbar">
                 <PsychometricTestNavbar signedOrganization={signedOrganization} />
                </ErrorBoundary>
            )}

            <div className="row p-3 px-5">
                <h1 className="col-md-12 col-12 mb-3" style={{ fontWeight: fontWeight.bold, fontSize: "1.5rem", color: colors.primaryGreenDark }}>
                    DECODING TALENT LANDSCAPE AND PERSONALITY MAPPING
                </h1>
                
                <h2 className="col-md-12 col-12 mb-3" style={{ fontWeight: fontWeight.bold, fontSize: "1rem", color: colors.primaryGreenDark }}>
                    
                    {isStudent ? "Building a strong academic culture for success" : "Building a strong workplace culture for success"}
                </h2>

                {/* Umang Wellness Psychometric Scale Section */}
                <div className="col-md-12 col-12 mb-4">
                    <h1 style={{ fontWeight: fontWeight.bold, fontSize: "1.5rem" }}>
                        {isStudent ? "  CAREER AND PERSONALITY ASSESSMENT" : "UMANG WELLNESS PSYCHOMETRIC SCALE"}
                      
                    </h1>
                    <h2 style={{ fontWeight: fontWeight.bold, fontSize: "1rem" }}>YOUR PERSONALITY</h2>
                    <p style={{ fontSize: "0.8rem" }}>
                        Your personality is shaped by many factors including your family, cultural experiences, along with environment.
                        Many fundamental characteristics of one's personality are innate. However, the origin of one's personality
                        is an important part of one's identity that can impact how happy one will be working in certain jobs or fields.
                    </p>
                </div>

                <div className="col-md-12 col-12 mb-4">
                    <h2 style={{ fontWeight: fontWeight.bold, fontSize: "1rem" }}>Personality Assessment Questionnaire</h2>
                    <p style={{ fontSize: "0.8rem" }}>
                        Describe yourself as you generally are now, not as you wish to be in the future. Describe yourself as you
                        honestly see yourself, in relation to other people you know of. Use the scale below to enter a number for
                        each statement. Read each statement carefully and then mark the appropriate response below.
                    </p>
                </div>

                <div className="col-md-12 col-12 mb-4">
                    <h1 style={{ fontWeight: fontWeight.bold, fontSize: "1rem", marginBottom: "1rem" }}>If you are ready, begin your test</h1>
                    <div className="text-right">
                      <Button type="submit" color="primary" disabled={isLoading} onClick={handleBeginClick} style={theme.formSubmit}>
                          {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Begin
                      </Button>
                    </div>
                </div>
            </div>
            <footer style={theme.psychometricTestFooter}>
                Powered by Umang Wellness
            </footer>
        </div>
    );
}

export default PsychometricTestStart;
